import React, { useState, useEffect, useRef } from 'react';
import { backendUrl, API_KEYS } from '../config';
import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, IconButton, Breadcrumbs, Paper, TextField, Button, Menu, MenuItem, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import FileUploadOutlined from '@mui/icons-material/FileUploadOutlined';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const FileExplorer = ({ onOpenApp, associations: propAssociations, currentUser }) => {
  const { t } = useTranslation();

  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [contextFile, setContextFile] = useState(null);
  const [newDialog, setNewDialog] = useState({ open: false, value: '' });
  const [selected, setSelected] = useState([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [renameDialog, setRenameDialog] = useState({ open: false, value: '', file: null });
  const [associations, setAssociations] = useState(propAssociations || []);
  const [associationsLoading, setAssociationsLoading] = useState(false);
  const [openFileError, setOpenFileError] = useState('');
  const [copyDialog, setCopyDialog] = useState({ open: false, value: '', src: '', type: '' });
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);

  let uuid7 = null;
  try {
    const userStr = window.localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      uuid7 = user.uuid7 || user.uuid || user.id || null;
    }
  } catch (e) {}

  useEffect(() => {
    if (!uuid7) {
      setError(t('fileexplorer.errors.userid'));
      setFiles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
    api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`)
      .then(res => setFiles(res.data))
      .catch(() => setError(t('fileexplorer.errors.load')))
      .finally(() => setLoading(false));
  }, [currentPath]);

  useEffect(() => {
    if (propAssociations) return;
    setAssociationsLoading(true);
    fetch(`${backendUrl}/api/config/associations`, {
      headers: {
        'x-api-key': API_KEYS.key,
        'x-api-secret': API_KEYS.secret,
        'accept': 'application/json'
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data && Array.isArray(data.associations)) {
          setAssociations(data.associations);
        }
      })
      .catch(() => setOpenFileError(t('fileexplorer.errors.associations')))
      .finally(() => setAssociationsLoading(false));
  }, [propAssociations, currentUser]);

  const goUp = () => {
    if (currentPath === '/') return;
    const parts = currentPath.split('/').filter(Boolean);
    parts.pop();
    setCurrentPath(parts.length === 0 ? '/' : '/' + parts.join('/'));
  };

  const openFolder = (name) => {
  //console.log('[FileExplorer] openFolder', name);
    setCurrentPath(currentPath === '/' ? `/${name}` : `${currentPath}/${name}`);
  };

  const handleMenu = (event, file) => {
  //console.log('[FileExplorer] handleMenu', file, event);
  if (event && event.stopPropagation) event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setContextFile(file);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setContextFile(null);
  };

  const handleNewFolder = () => {
    setNewDialog({ open: true, value: '' });
  };

  const confirmNewFolder = () => {
    if (!newDialog.value.trim()) return;
    let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
    let fullPath = relPath ? relPath + '/' + newDialog.value.trim() : newDialog.value.trim();
    setLoading(true);
    api.post('/create-folder', { uuid7, path: fullPath })
      .then(() => {
        setFiles(f => [...f, { name: newDialog.value.trim(), type: 'folder' }]);
        setNewDialog({ open: false, value: '' });
      })
      .catch(() => setError(t('fileexplorer.errors.create')))
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    setConfirmDelete(true);
    setMenuAnchor(null);
  };

  const doDelete = () => {
    if (!contextFile) return setConfirmDelete(false);
    let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
    let fullPath = relPath ? relPath + '/' + contextFile.name : contextFile.name;
    setLoading(true);
    const isFolder = contextFile.type === 'folder';
    const url = isFolder ? '/file/delete-folder' : '/file/delete-file';
    const method = 'DELETE';
    api({
      url,
      method,
      data: { uuid7, path: fullPath }
    })
      .then(() => {
        setFiles(f => f.filter(file => file.name !== contextFile.name));
        setContextFile(null);
      })
      .catch(() => setError(`Erreur lors de la suppression du ${isFolder ? 'dossier' : 'fichier'}.`))
      .finally(() => {
        setLoading(false);
        setConfirmDelete(false);
      });
  };

  const handleRename = () => {
    setRenameDialog({ open: true, value: contextFile?.name || '', file: contextFile });
    setMenuAnchor(null);
  };

  const doRename = () => {
    if (!renameDialog.file || !renameDialog.value.trim() || renameDialog.value === renameDialog.file.name) {
      setRenameDialog({ open: false, value: '', file: null });
      return;
    }
    let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
    let old_path = relPath ? relPath + '/' + renameDialog.file.name : renameDialog.file.name;
    let new_path = relPath ? relPath + '/' + renameDialog.value.trim() : renameDialog.value.trim();
    setLoading(true);
    api.put('/file/modify-file', { uuid7, old_path, new_path })
      .then(() => {
        setRenameDialog({ open: false, value: '', file: null });
        // Rafraîchir la liste après renommage
        let relPathRefresh = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
        api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPathRefresh)}`)
          .then(res => setFiles(res.data))
          .catch(() => setError(t('fileexplorer.errors.refresh')));
      })
      .catch(() => setError('Erreur lors du renommage.'))
      .finally(() => setLoading(false));
  };

  const handleFileOpen = async (file) => {
    setOpenFileError('');
    if (!file || file.type !== 'file') return;
    
    // Extraire l'extension
    const extMatch = file.name.match(/\.([a-zA-Z0-9]+)$/);
    const ext = extMatch ? extMatch[1].toLowerCase() : '';
    if (!ext) {
      setOpenFileError(t('fileexplorer.errors.extension'));
      return;
    }

    // Chercher l'association
    const assoc = associations.find(a => {
      if (!a.ext) return false;
      return a.ext.toLowerCase() === `.${ext}` || a.ext.toLowerCase() === ext;
    });

    if (!assoc?.app) {
      setOpenFileError(t('fileexplorer.errors.noapp', { ext }));
      return;
    }

    // Télécharger le fichier
    let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
    let filePath = relPath ? `${relPath}/${file.name}` : file.name;
    
    try {
      const response = await api.get(`/download-file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(filePath)}`);
      
      // Stocker le chemin et les données pour l'application
      window.localStorage.setItem('notepadApp_filePath', filePath);
      window.localStorage.setItem('notepadApp_fileContent', response.data?.content || '');
      
      // Ouvrir l'application associée
      if (typeof onOpenApp === 'function') {
        onOpenApp(assoc.app);
      }
    } catch (error) {
      setOpenFileError(t('fileexplorer.errors.openfile') + ': ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleCopy = (file) => {
    if (!file) return;
    let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
    let srcPath = relPath ? relPath + '/' + file.name : file.name;
    window.localStorage.setItem('fileExplorer_copy_src', srcPath);
    window.localStorage.setItem('fileExplorer_copy_type', file.type);
    setOpenFileError(t('fileexplorer.copy') + ' : ' + file.name);
  };

  const handlePaste = async () => {
    const srcPath = window.localStorage.getItem('fileExplorer_copy_src');
    const srcType = window.localStorage.getItem('fileExplorer_copy_type');
    if (!srcPath) {
      setOpenFileError(t('fileexplorer.errors.paste'));
      return;
    }
    let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
    let defaultName = srcPath.split('/').pop();
    setCopyDialog({ open: true, value: defaultName, src: srcPath, type: srcType });
  };

  // Fonctions d'import/export
  const handleImportClick = () => fileInputRef.current.click();

  const handleExportClick = () => {
    if (!selectedFile) return;
    const blob = new Blob([selectedFile.content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = selectedFile.name;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileInputChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
      const formData = new FormData();
      formData.append('file', file); // même nom que pour la copie
      formData.append('path', relPath); // même nom que pour la copie
      formData.append('user_id', uuid7); // même nom que pour la copie

      // NE PAS mettre de Content-Type, axios gère le boundary automatiquement
      await api.post('/upload-file', formData);

      // Rafraîchir la liste après succès
      const res = await api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`);
      setFiles(res.data);

    } catch (error) {
      console.error('Erreur complète:', error.response?.data);
      const errorDetails = error.response?.data?.detail?.map(
        err => `${err.loc?.join('.')}: ${err.msg}`
      ).join(' | ') || 'Erreur inconnue';
      setOpenFileError(`${t('fileexplorer.errors.upload_failed')}: ${errorDetails}`);
    }
    e.target.value = '';
  };


  // Fonctions de gestion des dialogues
  const handleCopyDialogCancel = () => setCopyDialog({ open: false, value: '', src: '', type: '' });

  const handleCopyDialogValidate = async () => {
    const { src, value, type } = copyDialog;
    if (!src || !value.trim()) {
      setOpenFileError(t('fileexplorer.errors.copy'));
      return;
    }
    try {
      setLoading(true);
      let relPath = currentPath === '/' ? '' : currentPath.replace(/^\//, '');
      let destPath = relPath ? `${relPath}/${value.trim()}` : value.trim();
      
      // Vérifier si la destination existe déjà
      const exists = files.some(f => f.name === value.trim());
      if (exists) {
        setOpenFileError(t('fileexplorer.errors.exists'));
        return;
      }
      
      // Télécharger le fichier source
      const srcResponse = await api.get(`/download-file?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(src)}`, 
        { responseType: 'blob' });
      
      // Créer un nouveau fichier FormData pour l'upload
      const formData = new FormData();
      formData.append('uuid7', uuid7);
      formData.append('path', destPath);
      formData.append('file', new Blob([srcResponse.data]), value.trim());
      
      // Uploader la copie
      await api.post('/upload-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // Rafraîchir la liste
      const refreshed = await api.get(`/files?uuid7=${encodeURIComponent(uuid7)}&path=${encodeURIComponent(relPath)}`);
      setFiles(refreshed.data);
      setOpenFileError(t('fileexplorer.copy_success') + ': ' + value.trim());
    } catch (error) {
      setOpenFileError(t('fileexplorer.errors.copy') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
      setCopyDialog({ open: false, value: '', src: '', type: '' });
    }
  };

  // Breadcrumbs logic
  const pathParts = currentPath.split('/').filter(Boolean);
  const breadcrumbPaths = pathParts.reduce((acc, part) => {
    const prev = acc.length > 0 ? acc[acc.length - 1] : '';
    acc.push(prev + '/' + part);
    return acc;
  }, []);

  // Filter files by search
  const filteredFiles = files.filter(f => !search || f.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <Paper sx={{ p: 2, minWidth: 350 }}>
      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <Button size="small" variant="outlined" onClick={goUp} onTouchStart={e => { e.stopPropagation(); goUp(); }} disabled={currentPath === '/'} startIcon={<ArrowUpwardIcon />}>{t('fileexplorer.up')}</Button>
        <TextField
          value={currentPath}
          size="small"
          label={t('fileexplorer.path')}
          sx={{ flex: 1 }}
          InputProps={{ readOnly: true }}
        />
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          label={t('fileexplorer.search')}
        />
        <IconButton onClick={handleNewFolder} title={t('fileexplorer.newfolder')}><AddIcon /></IconButton>
        <Tooltip title={t('fileexplorer.import')}>
          <span>
            <IconButton 
              onClick={handleImportClick}
              sx={{ mx: 1 }}
            >
              <FileUploadOutlined />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={t('fileexplorer.export')}>
          <span>
            <IconButton
              onClick={handleExportClick}
              disabled={!selectedFile || selectedFile.type !== 'file'}
              sx={{ mx: 1 }}
            >
              <FileDownloadOutlined />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <span style={{ cursor: 'pointer', color: '#1976d2' }} onClick={() => setCurrentPath('/')}>{t('fileexplorer.root')}</span>
        {pathParts.map((part, idx) => (
          <span
            key={idx}
            style={{ cursor: 'pointer', color: '#1976d2' }}
            onClick={() => setCurrentPath(breadcrumbPaths[idx])}
          >{part}</span>
        ))}
      </Breadcrumbs>
      <Box minHeight={0} overflow="auto">
        {loading ? (
          <Typography>Chargement...</Typography>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : (
          <List>
            {filteredFiles.map((file, idx) => (
              <ListItem
                key={idx}
                button
                onDoubleClick={() => file.type === 'folder' ? openFolder(file.name) : handleFileOpen(file)}
                onTouchStart={e => {
                  // Démarre le timer d'appui long
                  if (window.longTouchTimer) clearTimeout(window.longTouchTimer);
                  window.touchStartTime = Date.now();
                  window.longTouchFired = false;
                  window.longTouchMoved = false;
                  window.longTouchTimer = setTimeout(() => {
                    window.longTouchFired = true;
                    handleMenu(e, file);
                  }, 500); // 500ms = appui long
                }}
                onTouchMove={e => {
                  window.longTouchMoved = true;
                  if (window.longTouchTimer) {
                    clearTimeout(window.longTouchTimer);
                    window.longTouchTimer = null;
                  }
                }}
                onTouchEnd={e => {
                  if (window.longTouchTimer) {
                    clearTimeout(window.longTouchTimer);
                    window.longTouchTimer = null;
                  }
                  // Si ce n'est pas un appui long ET pas un mouvement, ouvrir/ouvrir dossier
                  if (!window.longTouchFired && !window.longTouchMoved) {
                    if (file.type === 'folder') openFolder(file.name);
                    else handleFileOpen(file);
                  }
                  window.longTouchFired = false;
                  window.longTouchMoved = false;
                }}
                onTouchCancel={e => {
                  if (window.longTouchTimer) {
                    clearTimeout(window.longTouchTimer);
                    window.longTouchTimer = null;
                  }
                  window.longTouchFired = false;
                  window.longTouchMoved = false;
                }}
                secondaryAction={
                  <IconButton onClick={e => handleMenu(e, file)}>
                    <MoreVertIcon />
                  </IconButton>
                }
              >
                <ListItemIcon>
                  {file.type === 'folder' ? <FolderIcon color="primary" /> : <InsertDriveFileIcon />}
                </ListItemIcon>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
            {filteredFiles.length === 0 && (<ListItem><ListItemText primary={t('fileexplorer.empty')} /></ListItem>)}
          </List>
        )}
      </Box>
      {openFileError && (
        <Typography color="error" sx={{ mt: 1 }}>{openFileError}</Typography>
      )}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {/* Action ouvrir si fichier */}
        {contextFile && contextFile.type === 'file' && (
          <MenuItem onClick={() => { handleFileOpen(contextFile); handleMenuClose(); }}>
            <InsertDriveFileIcon fontSize="small" sx={{ mr: 1 }} />{t('fileexplorer.open')}
          </MenuItem>
        )}
        <MenuItem onClick={handleRename}>
          <DriveFileRenameOutlineIcon fontSize="small" sx={{ mr: 1 }} />{t('fileexplorer.rename')}
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />{t('fileexplorer.delete')}
        </MenuItem>
        {contextFile && (
          <MenuItem onClick={() => { handleCopy(contextFile); handleMenuClose(); }}>
            <AddIcon fontSize="small" sx={{ mr: 1 }} />{t('fileexplorer.copy')}
          </MenuItem>
        )}
      </Menu>
      <Button variant="outlined" size="small" sx={{ mt: 1, mb: 1, ml: 2 }} onClick={handlePaste}>
        {t('fileexplorer.paste')}
      </Button>
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>{t('fileexplorer.confirmdelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('fileexplorer.confirmdelete.text')} {contextFile?.type === 'folder' ? t('fileexplorer.folder') : t('fileexplorer.file')} <b> {contextFile?.name}</b> ? {t('fileexplorer.confirmdelete.irreversible')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} color="primary">
            {t('fileexplorer.cancel')}
          </Button>
          <Button onClick={doDelete} color="error" variant="contained">
            {t('fileexplorer.delete')}
          </Button>
        </DialogActions>
      </Dialog>
      <Menu
        open={newDialog.open}
        anchorEl={null}
        onClose={() => setNewDialog({ open: false, value: '' })}
        anchorReference="none"
        sx={{ '& .MuiMenu-paper': { padding: 2 } }}
      >
        <Box display="flex" alignItems="center" gap={2} p={2}>
          <TextField
            label={t('fileexplorer.foldername')}
            value={newDialog.value}
            onChange={e => setNewDialog(d => ({ ...d, value: e.target.value }))}
            size="small"
          />
          <Button variant="contained" onClick={confirmNewFolder}>{t('fileexplorer.create')}</Button>
        </Box>
      </Menu>
      <Dialog open={renameDialog.open} onClose={() => setRenameDialog({ open: false, value: '', file: null })}>
        <DialogTitle>{t('fileexplorer.rename')} {renameDialog.file?.type === 'folder' ? t('fileexplorer.folder') : t('fileexplorer.file')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('fileexplorer.newname')} <b>{renameDialog.file?.name}</b> :
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('fileexplorer.newname')}
            value={renameDialog.value}
            onChange={e => setRenameDialog(d => ({ ...d, value: e.target.value }))}
            fullWidth
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameDialog({ open: false, value: '', file: null })} color="primary">
            {t('fileexplorer.cancel')}
          </Button>
          <Button onClick={doRename} color="primary" variant="contained">
            {t('fileexplorer.rename')}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={copyDialog.open} onClose={handleCopyDialogCancel}>
        <DialogTitle>{t('fileexplorer.copy')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('fileexplorer.copyname')} :
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label={t('fileexplorer.copyname')}
            value={copyDialog.value}
            onChange={e => setCopyDialog(d => ({ ...d, value: e.target.value }))}
            fullWidth
            variant="standard"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCopyDialogCancel} color="primary">{t('fileexplorer.cancel')}</Button>
          <Button onClick={handleCopyDialogValidate} color="primary" variant="contained">{t('fileexplorer.validate')}</Button>
        </DialogActions>
      </Dialog>
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
    </Paper>
  );
};

export default FileExplorer;